import React, { useState, useEffect, useRef, useCallback,useLayoutEffect } from "react";
import { LangContext } from "../../contexts/LangContext";
import { EmptyState } from "./EmptyState";
import { getCookie, getCookieJSON } from "../../services/cookie";
import { HiPlay, HiPause, HiTrash, HiCheck, HiSearch, HiCog, HiCalendar, HiGlobe, HiChartBar, HiLink, HiCheckCircle, HiDotsHorizontal, HiDuplicate, HiFilter, HiPencil, HiEye, HiX, HiDownload, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { FaInstagram, FaFacebook, FaLinkedin } from "react-icons/fa";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

import { createPortal } from "react-dom";


// ==================== Export Modal (Final - No Data Type) ====================
function ExportModal({ task, onClose, t, isMobile }) {
  const modalRef = useRef(null);
  useClickOutside(modalRef, () => onClose());
  
  const [format, setFormat] = useState('json');
  const [status, setStatus] = useState('success');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = "https://flautreach.com/flautreach/api";

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      
      const token = getCookie("authToken");
      if (!token) {
        setError(t.export.notAuthenticated || "Not authenticated");
        return;
      }

      // Формируем URL с параметрами
      const params = new URLSearchParams();
      params.append('status', status);

      const url = `${API_BASE}/tasks/${task.task_id}/export/${format}/?${params.toString()}`;

      // Для скачивания файла используем fetch + blob
      const res = await fetch(url, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Export failed (${res.status})`);
      }

      // Получаем blob
      const blob = await res.blob();
      
      // Создаем ссылку для скачивания
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Определяем имя файла из заголовков или генерируем
      const contentDisposition = res.headers.get('Content-Disposition');
      let filename = '';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      if (!filename) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        filename = `task_${task.task_id}_export_${timestamp}.${format === 'urls' ? 'txt' : format}`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      onClose();
    } catch (err) {
      console.error("Export error:", err);
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const formatOptions = [
    {
      id: 'json',
      icon: '📊',
      title: 'JSON',
      description: t.export.jsonDesc || 'Full data with all information',
    },
    {
      id: 'urls',
      icon: '🔗',
      title: 'URLs',
      description: t.export.urlsDesc || 'Only URLs in text file',
    },
    {
      id: 'csv',
      icon: '📈',
      title: 'CSV',
      description: t.export.csvDesc || 'Table format for Excel',
    },
  ];

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 10000, padding: isMobile ? 12 : 40
    }}
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}
    >
      <div ref={modalRef} style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: isMobile ? "100%" : 520,
        maxHeight: isMobile ? "90vh" : "85vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        overflow: "hidden",
        position: "relative",
        zIndex: 10001,
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: isMobile ? "20px 16px 16px" : "24px 24px 20px",
          borderBottom: "1.5px solid var(--border)",
          flexShrink: 0
        }}>
          <div>
            <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, margin: 0, color: "var(--dark)" }}>
              {t.export.title || "Export Results"}
            </h3>
            <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0 0" }}>
              {task.task_name || t.untitled}
            </p>
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }} 
            style={{
              padding: 8, borderRadius: 8, border: "1.5px solid var(--border)",
              background: "#fff", cursor: "pointer", color: "var(--muted)",
              display: "flex", alignItems: "center", flexShrink: 0, transition: ".15s"
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
          >
            <HiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1, overflow: "auto",
          padding: isMobile ? "16px" : "20px 24px",
        }}>
          {/* Format Selection */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: "var(--muted)",
              textTransform: "uppercase", letterSpacing: .5, marginBottom: 10
            }}>
              {t.export.format || "Format"}
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {formatOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setFormat(opt.id);
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: `1.5px solid ${format === opt.id ? "var(--blue)" : "var(--border)"}`,
                    background: format === opt.id ? "rgba(59,113,254,0.05)" : "#fff",
                    cursor: "pointer",
                    transition: ".15s",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    if (format !== opt.id) {
                      e.currentTarget.style.borderColor = "var(--blue)";
                      e.currentTarget.style.background = "rgba(59,113,254,0.02)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (format !== opt.id) {
                      e.currentTarget.style.borderColor = "var(--border)";
                      e.currentTarget.style.background = "#fff";
                    }
                  }}
                >
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{opt.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 600, color: "var(--dark)",
                      marginBottom: 2
                    }}>
                      {opt.title}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                      {opt.description}
                    </div>
                  </div>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${format === opt.id ? "var(--blue)" : "#d1d5db"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    background: format === opt.id ? "var(--blue)" : "transparent",
                  }}>
                    {format === opt.id && <HiCheck size={14} color="#fff" />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: "var(--muted)",
              textTransform: "uppercase", letterSpacing: .5, marginBottom: 10
            }}>
              {t.export.statusFilter || "Status Filter"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setStatus('success');
                }}
                style={{
                  padding: "10px",
                  borderRadius: 8,
                  border: `1.5px solid ${status === 'success' ? "var(--blue)" : "var(--border)"}`,
                  background: status === 'success' ? "rgba(59,113,254,0.05)" : "#fff",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  color: status === 'success' ? "var(--blue)" : "var(--dark)",
                  transition: ".15s",
                }}
              >
                ✅ {t.export.successOnly || "Success Only"}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setStatus('all');
                }}
                style={{
                  padding: "10px",
                  borderRadius: 8,
                  border: `1.5px solid ${status === 'all' ? "var(--blue)" : "var(--border)"}`,
                  background: status === 'all' ? "rgba(59,113,254,0.05)" : "#fff",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  color: status === 'all' ? "var(--blue)" : "var(--dark)",
                  transition: ".15s",
                }}
              >
                📊 {t.export.allResults || "All Results"}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 13,
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? "16px" : "16px 24px",
          borderTop: "1.5px solid var(--border)",
          display: "flex", gap: 8, justifyContent: "flex-end",
          flexShrink: 0,
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "1.5px solid var(--border)",
              background: "#fff",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
              color: "var(--dark)",
              transition: ".15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
          >
            {t.export.cancel || "Cancel"}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleExport();
            }}
            disabled={exporting}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "var(--blue)",
              cursor: exporting ? "not-allowed" : "pointer",
              fontSize: 14,
              fontWeight: 600,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 8,
              opacity: exporting ? 0.6 : 1,
              transition: ".15s",
            }}
          >
            {exporting ? (
              <>
                <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                {t.export.exporting || "Exporting..."}
              </>
            ) : (
              <>
                <HiDownload size={18} />
                {t.export.export || "Export"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== Конфиг платформ ====================
function getPlatformConfig(t) {
  return {
    instagram_pages: { icon: <FaInstagram size={16} color="#E4405F" />, color: "#E4405F", bg: "#E4405F15", label: t.instagramPages },
    instagram_posts: { icon: <FaInstagram size={16} color="#E4405F" />, color: "#E4405F", bg: "#E4405F15", label: t.instagramPosts },
    instagram_reels: { icon: <FaInstagram size={16} color="#E4405F" />, color: "#E4405F", bg: "#E4405F15", label: t.instagramReels },
    instagram_adb: { icon: <FaInstagram size={16} color="#E4405F" />, color: "#E4405F", bg: "#E4405F15", label: "Instagram ADB" },

    instagram_adb_spider: { icon: <FaInstagram size={16} color="#E4405F" />, color: "#E4405F", bg: "#E4405F15", label: "Instagram ADB SPIDER" },
    facebook_screenshot: { icon: <FaFacebook size={16} color="#1877F2" />, color: "#1877F2", bg: "#1877F215", label: "FB Screenshot" },
    facebook_posts: { icon: <FaFacebook size={16} color="#1877F2" />, color: "#1877F2", bg: "#1877F215", label: t.facebookPosts },
    facebook_pages: { icon: <FaFacebook size={16} color="#1877F2" />, color: "#1877F2", bg: "#1877F215", label: t.facebookPages },
    linkedin_posts: { icon: <FaLinkedin size={16} color="#0A66C2" />, color: "#0A66C2", bg: "#0A66C215", label: t.linkedinPosts },

  };
}

// ==================== Периоды ====================
function getPeriodLabels(t) {
  return {
    h: t.periodHour,
    d: t.periodDay,
    w: t.periodWeek,
    m: t.periodMonth,
    y: t.periodYear,
    any: t.periodAny,
  };
}

// ==================== Утилиты ====================
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function timeAgo(dateStr, t) {
  if (!dateStr) return "";
  const now = new Date();
  const d = new Date(dateStr);
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t.justNow;
  if (mins < 60) return t.minutesAgo.replace("{{count}}", mins);
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t.hoursAgo.replace("{{count}}", hours);
  const days = Math.floor(hours / 24);
  return t.daysAgo.replace("{{count}}", days);
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setIsMobile(window.innerWidth < 768), 150);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);
  return isMobile;
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler();
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

function formatTokens(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function calcScrapingTokens(task, userInfo) {
  if (!userInfo) return 0;
  
  const prices = {
    google_page: parseFloat(userInfo.price_per_google_page) || 0,
    facebook_post: parseFloat(userInfo.price_per_facebook_post) || 0,
    facebook_page: parseFloat(userInfo.price_per_facebook_page) || 0,
    linkedin_post: parseFloat(userInfo.price_per_linkedin_post) || 0,
    instagram_post: parseFloat(userInfo.price_per_instagram_post) || 0,
    instagram_page: parseFloat(userInfo.price_per_instagram_page) || 0,
    instagram_reels: parseFloat(userInfo.price_per_instagram_reel) || parseFloat(userInfo.price_per_instagram_post) || 0,
  };
  
  let totalCost = 0;
  
  const googleSuccess = task.google_jobs?.success || 0;
  totalCost += googleSuccess * prices.google_page;
  
  if (task.parse_pipeline) {
    task.parse_pipeline.forEach(stage => {
      const success = stage.success || 0;
      let price = 0;
      
      if (stage.job_type === 'facebook_posts') price = prices.facebook_post;
      else if (stage.job_type === 'facebook_pages') price = prices.facebook_page;
      else if (stage.job_type === 'linkedin_posts') price = prices.linkedin_post;
      else if (stage.job_type === 'instagram_posts') price = prices.instagram_post;
      else if (stage.job_type === 'instagram_pages') price = prices.instagram_page;
      else if (stage.job_type === 'instagram_reels') price = prices.instagram_reels;
      
      totalCost += success * price;
    });
  }
  
  return Math.round(totalCost * 100);
}


// ==================== Results Modal ====================
function ResultsModal({ task, onClose, t, isMobile }) {
  const modalRef = useRef(null);
  useClickOutside(modalRef, () => onClose());

  const reports = task.results?.reports || [];

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: isMobile ? 12 : 40
    }}>
      <div ref={modalRef} style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: isMobile ? "100%" : 680, 
        maxHeight: isMobile ? "90vh" : "85vh", 
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        {/* Header */}
        <div style={{ 
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", 
          padding: isMobile ? "20px 16px 16px" : "24px 24px 20px",
          borderBottom: "1.5px solid var(--border)",
          flexShrink: 0
        }}>
          <div>
            <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, margin: 0, color: "var(--dark)" }}>
              {t.resultsTitle}
            </h3>
            <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0 0" }}>
              {task.task_name || t.untitled}
            </p>
          </div>
          <button onClick={onClose} style={{
            padding: 8, borderRadius: 8, border: "1.5px solid var(--border)",
            background: "#fff", cursor: "pointer", color: "var(--muted)",
            display: "flex", alignItems: "center", flexShrink: 0, transition: ".15s"
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
          >
            <HiX size={20} />
          </button>
        </div>

        {/* Scrollable Reports List */}
        <div style={{ 
          flex: 1, overflow: "auto", 
          padding: isMobile ? "12px 16px 20px" : "16px 24px 24px" 
        }}>
          {reports.length > 0 ? (
            <div style={{ display: "grid", gap: 10 }}>
              {reports.map((report) => (
                <a 
                  key={report.id}
                  href={report.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 16, 
                    padding: isMobile ? "14px 16px" : "16px 20px",
                    borderRadius: 10, 
                    border: "1.5px solid var(--border)", 
                    background: "#fff",
                    textDecoration: "none", 
                    color: "inherit", 
                    transition: "all .15s"
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.background = "#f8faff"; 
                    e.currentTarget.style.borderColor = "var(--blue)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(59,113,254,.08)";
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.background = "#fff"; 
                    e.currentTarget.style.borderColor = "var(--border)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: 14, fontWeight: 600, color: "var(--dark)", 
                      marginBottom: 6, wordBreak: "break-word" 
                    }}>
                      {report.name}
                    </div>
                    <div style={{ 
                      fontSize: 12, color: "var(--muted)", 
                      display: "flex", alignItems: "center", gap: 6 
                    }}>
                      <HiCalendar size={14} />
                      <span>{formatDate(report.generated_at)}</span>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: "flex", alignItems: "center", gap: 8, flexShrink: 0 
                  }}>
                    <span style={{ 
                      fontSize: 12, color: "var(--blue)", fontWeight: 500,
                      display: isMobile ? "none" : "inline"
                    }}>
                      {t.openReport}
                    </span>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: "rgba(59,113,254,0.08)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "var(--blue)"
                    }}>
                      <HiDownload size={16} />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: "center", padding: "60px 20px", color: "var(--muted)" 
            }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: "var(--dark)" }}>
                {t.noReports}
              </div>
              <div style={{ fontSize: 13 }}>
                {t.reportsWillAppear}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ==================== Keyword Stats Modal ====================
function KeywordStatsModal({ task, onClose, t, isMobile }) {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [error, setError] = useState(null);
  
  useClickOutside(modalRef, () => onClose());

  const API_BASE = "https://flautreach.com/flautreach/api";

  useEffect(() => {
    fetchKeywordStats();
  }, []);

  const fetchKeywordStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getCookie("authToken");
      if (!token) {
        setError(t.notAuthenticated || "Not authenticated");
        return;
      }

      const res = await fetch(`${API_BASE}/tasks/${task.task_id}/keyword-stats/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch stats (${res.status})`);
      }

      const data = await res.json();
      setStats(data.stats || []);
    } catch (err) {
      console.error("Stats error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 10000, padding: isMobile ? 12 : 40
    }}>
      <div ref={modalRef} style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: isMobile ? "100%" : 800,
        maxHeight: isMobile ? "90vh" : "85vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: isMobile ? "20px 16px 16px" : "24px 24px 20px",
          borderBottom: "1.5px solid var(--border)",
          flexShrink: 0
        }}>
          <div>
            <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, margin: 0, color: "var(--dark)" }}>
              {t.keywordStats.title || "Keyword Statistics"}
            </h3>
            <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0 0" }}>
              {t.keywordStats.subtitle || "Statistics of passed filters by keywords"}
            </p>
          </div>
          <button 
            onClick={onClose} 
            style={{
              padding: 8, borderRadius: 8, border: "1.5px solid var(--border)",
              background: "#fff", cursor: "pointer", color: "var(--muted)",
              display: "flex", alignItems: "center", flexShrink: 0, transition: ".15s"
            }}
          >
            <HiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1, overflow: "auto",
          padding: isMobile ? "16px" : "20px 24px",
        }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <div className="spinner" />
            </div>
          ) : error ? (
            <div style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 13,
            }}>
              {error}
            </div>
          ) : stats.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "40px 20px", color: "var(--muted)"
            }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: "var(--dark)" }}>
                {t.keywordStats.noStats || "No statistics available"}
              </div>
              <div style={{ fontSize: 13 }}>
                {t.keywordStats.noStatsDesc || "Statistics will appear when filtering is complete"}
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {/* Table Header */}
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "2fr 1fr 1fr" : "3fr 1fr 1fr",
                gap: 8,
                padding: "10px 14px",
                background: "#f8fafc",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: .5,
              }}>
                <div>{t.keywordStats.keyword || "Keyword"}</div>
                <div style={{ textAlign: "center" }}>{t.keywordStats.lastPage || "Last Page"}</div>
                <div style={{ textAlign: "center" }}>{t.keywordStats.passed || "Passed"}</div>
              </div>

              {/* Table Rows */}
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "2fr 1fr 1fr" : "3fr 1fr 1fr",
                    gap: 8,
                    padding: "12px 14px",
                    background: "#fff",
                    borderRadius: 8,
                    border: "1.5px solid var(--border)",
                    fontSize: 13,
                    transition: ".15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--blue)";
                    e.currentTarget.style.background = "rgba(59,113,254,0.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border)";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "var(--dark)", wordBreak: "break-word" }}>
                      {stat.keyword}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                      {formatDate(stat.created_at)}
                    </div>
                  </div>
                  <div style={{ textAlign: "center", color: "var(--dark)", fontWeight: 500 }}>
                    {stat.last_page || 0}
                  </div>
                  <div style={{ 
                    textAlign: "center", 
                    color: stat.passed_count > 0 ? "#15803d" : "var(--muted)",
                    fontWeight: 700 
                  }}>
                    {stat.passed_count || 0}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== Reels Configuration View ====================
function ReelsConfigView({ reelsConfig, isMobile, t }) {
  if (!reelsConfig) return null;
  
  const metrics = reelsConfig.metrics_filter || {};
  const hasMetrics = Object.values(metrics).some(v => v !== null && v !== undefined);
  const hasConfig = reelsConfig.need_filter || reelsConfig.generate_summary || 
                     reelsConfig.need_description || reelsConfig.sort_by ||
                     reelsConfig.top_reels_count || reelsConfig.engagement_formula;

  if (!hasMetrics && !hasConfig) return null;

  return (
    <div>
      <div style={{ 
        fontSize: 11, fontWeight: 700, color: "var(--muted)", 
        textTransform: "uppercase", letterSpacing: .5, marginBottom: 8,
        display: "flex", alignItems: "center", gap: 4 
      }}>
        <HiCog size={12} /> {t.reelsConfig}
      </div>
      <div style={{ 
        background: "#fff", borderRadius: 10, border: "1.5px solid var(--border)", 
        padding: "14px 16px", display: "grid", gap: 12 
      }}>
        
        {/* Configuration Badges */}
        {hasConfig && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {reelsConfig.sort_by && (
              <span style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: "#f0f4ff", color: "var(--blue)", border: "1px solid rgba(59,113,254,.2)"
              }}>
                {t.sortBy}: {reelsConfig.sort_by}
              </span>
            )}
            {reelsConfig.top_reels_count && (
              <span style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: "#f0f4ff", color: "var(--blue)", border: "1px solid rgba(59,113,254,.2)"
              }}>
                {t.topReels}: {reelsConfig.top_reels_count}
              </span>
            )}
            {reelsConfig.engagement_formula && (
              <span style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: "#f0f4ff", color: "var(--blue)", border: "1px solid rgba(59,113,254,.2)"
              }}>
                {t.engagementFormula}: {reelsConfig.engagement_formula}
              </span>
            )}
            {reelsConfig.need_filter && (
              <span style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0"
              }}>
                {t.metricsFiltering}
              </span>
            )}
            {reelsConfig.ai_pass_keyword && (
              <span style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0",
                display: "flex", alignItems: "center", gap: 4
              }}>
                {t.passKeyword}: <code style={{ fontWeight: 700 }}>"{reelsConfig.ai_pass_keyword}"</code>
              </span>
            )}
            {reelsConfig.generate_summary && (
              <span style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                background: "#ede9fe", color: "#5b21b6", border: "1px solid #ddd6fe"
              }}>
                🤖{t.aiSummary}
              </span>
            )}
            
          </div>
        )}

        {/* Metrics Filters */}
        {hasMetrics && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", 
              textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
              {t.metricsFilter}
            </div>
            <div style={{ 
              display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", 
              gap: 6 
            }}>
              {metrics.min_views !== null && metrics.min_views !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#f0fdf4",
                  border: "1px solid #bbf7d0", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.minViews}
                  </div>
                  <div style={{ fontWeight: 700, color: "#15803d" }}>
                    {metrics.min_views?.toLocaleString()}
                  </div>
                </div>
              )}
              {metrics.max_views !== null && metrics.max_views !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#fef2f2",
                  border: "1px solid #fecaca", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.maxViews}
                  </div>
                  <div style={{ fontWeight: 700, color: "#991b1b" }}>
                    {metrics.max_views?.toLocaleString()}
                  </div>
                </div>
              )}
              {metrics.min_likes !== null && metrics.min_likes !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#f0fdf4",
                  border: "1px solid #bbf7d0", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.minLikes}
                  </div>
                  <div style={{ fontWeight: 700, color: "#15803d" }}>
                    {metrics.min_likes?.toLocaleString()}
                  </div>
                </div>
              )}
              {metrics.max_likes !== null && metrics.max_likes !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#fef2f2",
                  border: "1px solid #fecaca", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.maxLikes}
                  </div>
                  <div style={{ fontWeight: 700, color: "#991b1b" }}>
                    {metrics.max_likes?.toLocaleString()}
                  </div>
                </div>
              )}
              {metrics.min_comments !== null && metrics.min_comments !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#f0fdf4",
                  border: "1px solid #bbf7d0", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.minComments}
                  </div>
                  <div style={{ fontWeight: 700, color: "#15803d" }}>
                    {metrics.min_comments?.toLocaleString()}
                  </div>
                </div>
              )}
              {metrics.max_comments !== null && metrics.max_comments !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#fef2f2",
                  border: "1px solid #fecaca", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.maxComments}
                  </div>
                  <div style={{ fontWeight: 700, color: "#991b1b" }}>
                    {metrics.max_comments?.toLocaleString()}
                  </div>
                </div>
              )}
              {metrics.min_engagement_rate !== null && metrics.min_engagement_rate !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#f0fdf4",
                  border: "1px solid #bbf7d0", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.minEngagement}
                  </div>
                  <div style={{ fontWeight: 700, color: "#15803d" }}>
                    {metrics.min_engagement_rate}%
                  </div>
                </div>
              )}
              {metrics.max_engagement_rate !== null && metrics.max_engagement_rate !== undefined && (
                <div style={{ 
                  padding: "8px 10px", borderRadius: 8, background: "#fef2f2",
                  border: "1px solid #fecaca", fontSize: 12 
                }}>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginBottom: 2 }}>
                    {t.maxEngagement}
                  </div>
                  <div style={{ fontWeight: 700, color: "#991b1b" }}>
                    {metrics.max_engagement_rate}%
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prompts Section */}
        {(reelsConfig.ai_filter_prompt || reelsConfig.summary_prompt) && (
          <div style={{ display: "grid", gap: 10 }}>
            {reelsConfig.ai_filter_prompt && (
              <div style={{ 
                background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", 
                padding: "12px 14px" 
              }}>
                <div style={{ 
                  fontSize: 10, fontWeight: 700, color: "#6366f1", 
                  textTransform: "uppercase", letterSpacing: .8, marginBottom: 6, 
                  display: "flex", alignItems: "center", gap: 4 
                }}>
                  🧠{t.filteringPrompt}
                </div>
                <div style={{ 
                  fontSize: 12, color: "#475569", whiteSpace: "pre-wrap", 
                  lineHeight: 1.6, wordBreak: "break-word", maxHeight: 100, 
                  overflow: "auto" 
                }}>
                  {reelsConfig.ai_filter_prompt}
                </div>
              </div>
            )}
            
            {reelsConfig.summary_prompt && (
              <div style={{ 
                background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", 
                padding: "12px 14px" 
              }}>
                <div style={{ 
                  fontSize: 10, fontWeight: 700, color: "#8b5cf6", 
                  textTransform: "uppercase", letterSpacing: .8, marginBottom: 6, 
                  display: "flex", alignItems: "center", gap: 4 
                }}>
                  🧠{t.summaryPrompt}
                </div>
                <div style={{ 
                  fontSize: 12, color: "#475569", whiteSpace: "pre-wrap", 
                  lineHeight: 1.6, wordBreak: "break-word", maxHeight: 100, 
                  overflow: "auto" 
                }}>
                  {reelsConfig.summary_prompt}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}




// ==================== Кнопка Actions Menu (портал + fixed позиционирование) ====================
function ActionsMenu({ onEdit, onDuplicate, onCreateFilter, onExport, onKeywordStats, isMobile, t, task }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const MENU_WIDTH = 220;
  const MARGIN = 8;

  // Пересчитываем позицию при открытии и на resize/scroll
  const updatePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();

    // По умолчанию: правый край меню совпадает с правым краем кнопки
    let left = rect.right - MENU_WIDTH;
    // Не даём вылезти за левый край экрана
    if (left < MARGIN) left = MARGIN;
    // Не даём вылезти за правый край экрана
    if (left + MENU_WIDTH > window.innerWidth - MARGIN) {
      left = window.innerWidth - MENU_WIDTH - MARGIN;
    }

    let top = rect.bottom + 4;
    // Если снизу не влезает (примерная высота меню ~260px) — открываем вверх
    const estimatedHeight = 260;
    if (top + estimatedHeight > window.innerHeight - MARGIN) {
      top = rect.top - estimatedHeight - 4;
      if (top < MARGIN) top = MARGIN; // на крайний случай
    }

    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handle = () => updatePosition();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true); // true — ловим скролл любого предка
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [isOpen]);

  // Закрытие по клику вне
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Закрытие по Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  const isFilteringTask = task?.task_type === "filtering" ||
                          task?.task_type === "scraping_filtering" ||
                          task?.task_type === "scraping_and_filtering";
  const isScrapingTask = task?.task_type === "scraping";

  const menuItems = [
    { icon: <HiPencil size={14} />, label: t.rename?.label || "Rename", onClick: () => { onEdit(); setIsOpen(false); } },
    { icon: <HiDownload size={14} />, label: t.export?.exportResults || "Export Results", onClick: () => { onExport(); setIsOpen(false); } },
  ];

  if (isFilteringTask) {
    menuItems.push({
      icon: <HiChartBar size={14} />,
      label: t.keywordStats?.title || "Keyword Statistics",
      onClick: () => { onKeywordStats(); setIsOpen(false); }
    });
  }

  menuItems.push({ icon: <HiDuplicate size={14} />, label: t.duplicate || "Duplicate", onClick: () => { onDuplicate(); setIsOpen(false); } });

  if (isScrapingTask) {
    menuItems.splice(2, 0, {
      icon: <HiFilter size={14} />,
      label: t.createFilter || "Create Filter",
      onClick: () => { onCreateFilter(); setIsOpen(false); }
    });
  }

  return (
    <div ref={buttonRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(prev => !prev);
        }}
        style={{
          padding: isMobile ? "5px 6px" : "6px 8px",
          borderRadius: 8,
          border: `1.5px solid ${isOpen ? "var(--blue)" : "var(--border)"}`,
          background: isOpen ? "rgba(59,113,254,0.05)" : "#fff",
          cursor: "pointer",
          color: isOpen ? "var(--blue)" : "var(--muted)",
          display: "flex",
          alignItems: "center",
          transition: "all .15s",
        }}
        title={t.moreActions || "More actions"}
      >
        <HiDotsHorizontal size={isMobile ? 14 : 16} />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            background: "#fff",
            borderRadius: 10,
            border: "1.5px solid var(--border)",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            width: MENU_WIDTH,
            zIndex: 100000,
            overflow: "hidden",
            padding: "4px",
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                item.onClick();
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                color: "var(--dark)",
                transition: ".15s",
                textAlign: "left",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(59,113,254,0.08)";
                e.currentTarget.style.color = "var(--blue)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--dark)";
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ==================== Force Stop Banner ====================
function ForceStopBanner({ status, onTopUp, t }) {
  const isAI = status === 'forcestop_aitokens';
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 20px',
      background: isAI ? '#fffbeb' : '#eff6ff',
      borderBottom: `1.5px solid ${isAI ? '#fde68a' : '#bfdbfe'}`,
    }}>
      <span style={{ fontSize: 22, flexShrink: 0 }}>
        {isAI ? '🤖' : '🕸️'}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
          {isAI ? t.noAITokensTitle : t.noScrapingTokensTitle}
        </div>
      </div>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onTopUp(isAI ? 'ai' : 'scraping');
        }}
        style={{
          flexShrink: 0,
          padding: '7px 14px',
          borderRadius: 8,
          border: 'none',
          background: isAI ? '#f59e0b' : '#3b82f6',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {t.topUp}
      </button>
    </div>
  );
}

// ==================== Карточка таска ====================
function TaskCard({ task, onStart, onStop, onDelete, onVisualize, onEdit, onDuplicate, onCreateFilter, t, onForceStopTopUp, userInfo }) {
  const [expanded, setExpanded] = useState(false);
  const [showAllValues, setShowAllValues] = useState({});
  const [showResults, setShowResults] = useState(false);
  const isMobile = useIsMobile();
  
  const PLATFORM_CONFIG = getPlatformConfig(t);
  const PERIOD_LABELS = getPeriodLabels(t);
  
  const subtype = task.search_config?.subtype || task.filtering_config?.subtype;
  const platform = PLATFORM_CONFIG[subtype] || {};
  const isRunning = task.task_status === "running" || task.task_status === "pending";
  const isDone = task.task_status === "done" || task.task_status === "completed";
  const isFailed = task.task_status === "failed";
  const isPaused = task.task_status === "paused";
  const isForceStopped = task.task_status === 'forcestop_aitokens' || task.task_status === 'forcestop_scrtokens';
  const isForceStopAI = task.task_status === 'forcestop_aitokens';
  const isForceStopScraping = task.task_status === 'forcestop_scrtokens';

  const isInstagramReels = subtype === "instagram_reels";
  const isFilteringTask = task.task_type === "filtering";
  const isScrapingAndFiltering = task.task_type === "scraping_filtering" || task.task_type === "scraping_and_filtering";
  const isScrapingTask = task.task_type === "scraping";
  const hasFiltering = isFilteringTask || isScrapingAndFiltering;
  const hasScraping = isScrapingTask || isScrapingAndFiltering;

  const totalJobs = task.total_jobs || 0;
  const completedJobs = task.total_completed || 0;
  const failedJobs = task.total_failed || 0;
  const progress = task.progress_percent || 0;

  const googleJobs = task.google_jobs || {};
  const parsePipeline = task.parse_pipeline || [];
  const filteringStats = task.filtering || {};
  const filteringConfig = task.filtering_config || {};

  const additionalInfo = task.additional_info || {};
  const hasAdditionalInfo = Object.keys(additionalInfo).length > 0 && Object.values(additionalInfo).some(v => v !== null && v !== undefined);
  const instagramPostsIncluded = additionalInfo?.instagram_posts_included === "True" || additionalInfo?.instagram_posts_included === true;
  const isInstagramPages = subtype === "instagram_pages";

  const filterRunNumTask = additionalInfo?.filterRunNumTask;

  const fTotal = filteringStats?.total || 0;
  const fPassed = filteringStats?.passed || 0;
  const fRejected = filteringStats?.rejected || 0;
  const fAIPassed = filteringStats?.ai_passed || 0;
  const fAIRejected = filteringStats?.ai_rejected || 0;
  const fPending = filteringStats?.pending || 0;
  const fRunning = filteringStats?.running || 0;
  const fDone = filteringStats?.done || 0;
  const fFailed = filteringStats?.failed || 0;
  const fAITokens = filteringStats?.ai_tokens_spent || 0;

  const filterRules = filteringConfig?.filters?.rules || {};
  const matchType = filteringConfig?.filters?.match_type || "all";
  const reelsConfig = filteringConfig?.filters?.reels_config || {};

  const scrapingTokens = calcScrapingTokens(task, userInfo);
  const [showExport, setShowExport] = useState(false); // Новое состояние
  const [showKeywordStats, setShowKeywordStats] = useState(false); // Новое состояние

    const handleKeywordStats = () => {
    setShowKeywordStats(true);
  };


  const formatRuleValue = (op, val, fieldKey) => {
    switch (op) {
      case "contains_any_row":
      case "not_contains_any_row": {
        let items = [];
        if (typeof val === "string" && val.includes("\n")) {
          items = val.split("\n").map(s => s).filter(Boolean);
        } else if (Array.isArray(val)) {
          items = val;
        } else {
          items = [String(val || "")];
        }
        
        const isExpanded = showAllValues[fieldKey];
        const maxShow = 2;
        
        if (items.length <= maxShow || isExpanded) {
          return { text: items.map(v => `"${v}"`).join(", "), hasMore: false, count: items.length };
        }
        return { text: items.slice(0, maxShow).map(v => `"${v}"`).join(", "), hasMore: true, hiddenCount: items.length - maxShow, count: items.length };
      }
      case "between":
        if (typeof val === "object" && val !== null) {
          return { text: t.between.replace("{{min}}", val.min || "?").replace("{{max}}", val.max || "?"), hasMore: false, count: 0 };
        }
        return { text: String(val || ""), hasMore: false, count: 0 };
      case "greater_than":
        return { text: t.greaterThan.replace("{{val}}", val), hasMore: false, count: 0 };
      case "less_than":
        return { text: t.lessThan.replace("{{val}}", val), hasMore: false, count: 0 };
      default:
        return { text: String(val ?? ""), hasMore: false, count: 0 };
    }
  };
  const handleExport = () => {
    setShowExport(true);
  };

  const getRuleDisplay = (op, val, fieldKey) => {
    switch (op) {
      case "equals": return `${t.equals} "${val}"`;
      case "contains": return `${t.contains} "${val}"`;
      case "not_contains": return `${t.doesNotContain} "${val}"`;
      case "contains_any_row": {
        const f = formatRuleValue(op, val, fieldKey);
        return f.count === 1 ? `${t.containsAny}: ${f.text}` : `${t.containsAnyOf} ${f.count} ${t.valuesCount}: ${f.text}`;
      }
      case "not_contains_any_row": {
        const f = formatRuleValue(op, val, fieldKey);
        return f.count === 1 ? `${t.excludesAll}: ${f.text}` : `${t.excludesAllOf} ${f.count} ${t.valuesCount}: ${f.text}`;
      }
      case "is_not_empty": return t.isNotEmpty;
      case "greater_than": return t.greaterThan.replace("{{val}}", val);
      case "less_than": return t.lessThan.replace("{{val}}", val);
      case "between": return formatRuleValue(op, val, fieldKey).text;
      case "is_true": return t.isTrue;
      case "is_false": return t.isFalse;
      default: return `${op}: ${val}`;
    }
  };

  const isNegativeOp = (op) => op?.includes("not") || op === "is_false" || op === "less_than";
  const isPositiveOp = (op) => op === "is_true" || op === "is_not_empty" || op === "greater_than";

  const toggleShowAll = (fieldKey) => {
    setShowAllValues(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const getStatusLabel = () => {
    if (isForceStopAI) return t.noAITokens;
    if (isForceStopScraping) return t.noScrapingTokens;
    if (isPaused) return t.paused;
    return t[task.task_status] || task.task_status || t.unknown;
  };

  const getStatusColor = () => {
    if (isForceStopped) return { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" };
    if (isRunning) return { bg: "#f0fdf4", color: "#15803d", dot: "#22c55e" };
    if (isPaused) return { bg: "#fff7ed", color: "#c2410c", dot: "#f97316" };
    if (isDone) return { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" };
    if (isFailed) return { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" };
    return { bg: "#f3f4f6", color: "#6b7280", dot: "#9ca3af" };
  };

  const statusStyle = getStatusColor();

  return (
    <div style={{
      background: "#fff", borderRadius: 14,
      border: `1.5px solid ${isForceStopped ? '#f59e0b' : isRunning ? "var(--blue)" : isPaused ? "#f97316" : isDone ? "#bbf7d0" : isFailed ? "#fecaca" : "var(--border)"}`,
      overflow: "hidden", transition: ".2s",
    }}
      onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.boxShadow = "0 4px 20px rgba(59,113,254,.1)"; }}
      onMouseLeave={(e) => { if (!isMobile) e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* FORCE STOP BANNER */}
      {isForceStopped && (
        <ForceStopBanner status={task.task_status} onTopUp={onForceStopTopUp} t={t} />
      )}

      {/* Main row */}
      <div style={{ padding: isMobile ? "14px 14px" : "18px 20px", cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-start", justifyContent: "space-between", gap: isMobile ? 10 : 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <h3 style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "var(--dark)", margin: 0 }}>{task.task_name || t.untitled}</h3>

              {/* Тип задачи */}
              {isFilteringTask && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: "#fef3c7", border: "1px solid #fde68a", fontSize: 11, fontWeight: 600, color: "#92400e" }}>
                  <HiFilter size={12} /> {t.filtering}
                </span>
              )}
              {isScrapingAndFiltering && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: "#ede9fe", border: "1px solid #ddd6fe", fontSize: 11, fontWeight: 600, color: "#5b21b6" }}>
                  <HiSearch size={12} />+<HiFilter size={12} /> {t.scrapingAndFiltering}
                </span>
              )}
              {isScrapingTask && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: "#e0f2fe", border: "1px solid #bae6fd", fontSize: 11, fontWeight: 600, color: "#0369a1" }}>
                  <HiSearch size={12} /> {t.scraping}
                </span>
              )}
              
              {/* Платформа */}
              {platform?.icon && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: platform.bg, fontSize: 11, fontWeight: 600, color: platform.color }}>
                  {platform.icon} {platform.label || subtype}
                </span>
              )}
              
              {/* Instagram Posts Included */}
              {isInstagramPages && instagramPostsIncluded && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 6, background: "#f0fdf4", border: "1px solid #bbf7d0", fontSize: 12, fontWeight: 600, color: "#15803d" }}>
                  {t.authorPagesLabel}
                </span>
              )}
            </div>
            
            {/* Вторая строка информации */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--muted)", flexWrap: "wrap" }}>
              <HiCalendar size={12} />
              <span>{timeAgo(task.created_at, t)}</span>
              {task.scraped_links_count > 0 && (
                <>
                  <span style={{ margin: "0 4px" }}>·</span>
                  <HiLink size={12} />
                  <span>{task.scraped_links_count.toLocaleString()} {t.links}</span>
                </>
              )}
              {filterRunNumTask && (
                <>
                  <span style={{ margin: "0 4px" }}>·</span>
                  <span>{filterRunNumTask} {t.items}</span>
                </>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: isMobile ? "space-between" : "flex-end", gap: isMobile ? 6 : 10, flexShrink: 0, flexWrap: "wrap" }}>
            {/* Статус */}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: isMobile ? "4px 8px" : "5px 10px", borderRadius: 20, fontSize: isMobile ? 10 : 11, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color, whiteSpace: "nowrap" }}>
              <span style={{ width: isMobile ? 6 : 7, height: isMobile ? 6 : 7, borderRadius: "50%", background: statusStyle.dot }} />
              {getStatusLabel()}
            </span>

            {/* Кнопки управления */}
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
              {/* Check Results Button - show only for completed reels tasks */}
              {isInstagramReels && (
                <button 
                  onClick={() => setShowResults(true)} 
                  title={t.checkResults}
                  style={{ 
                    padding: isMobile ? "5px 8px" : "6px 10px", 
                    borderRadius: 8, 
                    border: "1.5px solid #c7d2fe", 
                    background: "#fff", 
                    cursor: "pointer", 
                    color: "#4f46e5", 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 4, 
                    fontSize: isMobile ? 10 : 12, 
                    fontWeight: 600 
                  }}
                >
                  <HiEye size={isMobile ? 12 : 14} /> {t.checkResults}
                </button>
              )}
              
              {isForceStopped ? (
                <button disabled style={{ padding: isMobile ? "5px 8px" : "6px 10px", borderRadius: 8, border: "1.5px solid var(--border)", background: "#f3f4f6", cursor: "not-allowed", color: "#9ca3af", display: "flex", alignItems: "center", gap: 4, fontSize: isMobile ? 10 : 12, fontWeight: 600, opacity: 0.5 }}>
                  <HiPlay size={isMobile ? 12 : 14} /> {t.start}
                </button>
              ) : isRunning ? (
                <button onClick={() => onStop(task.task_id)} title={t.stop} style={{ padding: isMobile ? "5px 8px" : "6px 10px", borderRadius: 8, border: "1.5px solid #fde68a", background: "#fff", cursor: "pointer", color: "#d97706", display: "flex", alignItems: "center", gap: 4, fontSize: isMobile ? 10 : 12, fontWeight: 600 }}>
                  <HiPause size={isMobile ? 12 : 14} /> {t.stop}
                </button>
              ) : (
                <button onClick={() => onStart(task.task_id)} title={t.start} style={{ padding: isMobile ? "5px 8px" : "6px 10px", borderRadius: 8, border: "1.5px solid #bbf7d0", background: "#fff", cursor: "pointer", color: "#16a34a", display: "flex", alignItems: "center", gap: 4, fontSize: isMobile ? 10 : 12, fontWeight: 600 }}>
                  <HiPlay size={isMobile ? 12 : 14} /> {t.start}
                </button>
              )}
              <button onClick={() => onVisualize(task)} title={t.visualization} style={{ padding: isMobile ? "5px 8px" : "6px 10px", borderRadius: 8, border: "1.5px solid #bfdbfe", background: "#fff", cursor: "pointer", color: "var(--blue)", display: "flex", alignItems: "center", gap: 4, fontSize: isMobile ? 10 : 12, fontWeight: 600 }}>
                <HiChartBar size={isMobile ? 12 : 14} /> {t.visualization}
              </button>
              <button onClick={() => onDelete(task.task_id)} title={t.delete} style={{ padding: isMobile ? "5px 6px" : "6px 8px", borderRadius: 8, border: "1.5px solid var(--border)", background: "#fff", cursor: "pointer", color: "var(--muted)", display: "flex", alignItems: "center", transition: ".15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.borderColor = "#fecaca"; e.currentTarget.style.color = "#dc2626"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--muted)"; }}
              >
                <HiTrash size={isMobile ? 12 : 14} /> {t.delete}
              </button>
            </div>
            
            
             <ActionsMenu 
              onEdit={() => onEdit(task)} 
              onDuplicate={() => onDuplicate(task)} 
              onCreateFilter={() => onCreateFilter(task)}
              onExport={handleExport}  // Добавьте этот проп
              onKeywordStats={handleKeywordStats}  // Добавьте этот проп
              isMobile={isMobile} 
              t={t}
              task={task}  // Добавьте этот проп
            />

            {showExport && (
              <ExportModal 
                task={task}
                onClose={() => setShowExport(false)}
                t={t}
                isMobile={isMobile}
              />
            )}

          </div>
        </div>

        {/* Прогресс-бар */}
        {totalJobs > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: "var(--muted)" }}>{completedJobs}/{totalJobs} {t.jobsCompleted}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: isRunning ? "var(--blue)" : "var(--muted)" }}>{Math.round(progress)}%</span>
            </div>
            <div style={{ width: "100%", height: 5, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 3, background: isRunning ? "linear-gradient(135deg, #3b71fe 0%, #01d0fd 100%)" : isDone ? "#22c55e" : isPaused ? "#f97316" : "#e5e7eb", width: `${Math.min(progress, 100)}%`, transition: "width .3s" }} />
            </div>
            {failedJobs > 0 && <div style={{ fontSize: 13, color: "#ef4444", marginTop: 2 }}>{failedJobs} {t.failed}</div>}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 8 }}>
          <span style={{ fontSize: 10, color: "var(--muted)", transform: expanded ? "rotate(180deg)" : "rotate(0)", display: "inline-block", transition: ".2s" }}>▼</span>
        </div>
      </div>

      {/* EXPANDED */}
      {expanded && (
        <div style={{ borderTop: "1.5px solid var(--border)", padding: isMobile ? "14px" : "18px 20px", background: "#fafbff" }}>
          <div style={{ display: "grid", gap: 14 }}>

            {/* ===== REELS CONFIG ===== */}
            {isInstagramReels && reelsConfig && (
              <ReelsConfigView reelsConfig={reelsConfig} isMobile={isMobile} t={t} />
            )}

            {/* ===== FILTERING CONFIG (non-reels) ===== */}
            {hasFiltering && filteringConfig && !isInstagramReels && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 10, display: "flex", alignItems: "center", gap: 4 }}>
                  <HiFilter size={12} /> {t.filteringConfig}
                </div>
                <div style={{ background: "#fff", borderRadius: 10, border: "1.5px solid var(--border)", padding: "14px 16px", display: "grid", gap: 10 }}>
                  
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: filteringConfig.use_ai ? "#f0fdf4" : "#f3f4f6", fontSize: 12, fontWeight: 600, color: filteringConfig.use_ai ? "#15803d" : "#6b7280" }}>
                      <span>{filteringConfig.use_ai ? "🤖" : "🚫"}</span>
                      {filteringConfig.use_ai ? t.aiFilteringEnabled : t.aiFilteringDisabled}
                    </div>
                    
                    {filteringConfig.use_ai && filteringConfig.ai_pass_keyword && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 20, background: "#f0f4ff", fontSize: 12, fontWeight: 500, color: "var(--blue)" }}>
                        <span></span>
                        {t.passKeyword}: <code style={{ fontWeight: 700 }}>"{filteringConfig.ai_pass_keyword}"</code>
                      </div>
                    )}
                    
                    {filteringConfig.check_only_on_first_row && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 20, background: "#fef3c7", fontSize: 12, fontWeight: 500, color: "#92400e" }}>
                        <span></span> {t.checkFirstRowOnly}
                      </div>
                    )}

                    <div style={{
                      display: "flex", alignItems: "center", gap: 4,
                      padding: "5px 12px", borderRadius: 20,
                      background: matchType === "all" ? "#eff6ff" : "#fffbeb",
                      border: `1px solid ${matchType === "all" ? "#bfdbfe" : "#fde68a"}`,
                      fontSize: 12, fontWeight: 500,
                      color: matchType === "all" ? "#1e40af" : "#92400e",
                    }}>
                      <span>{matchType === "all" ? "" : ""}</span>
                      {matchType === "all" ? t.allRules : t.anyRule}
                    </div>
                  </div>

                  {Object.keys(filterRules).length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                        {t.filterRules}
                      </div>
                      
                      <div style={{ display: "grid", gap: 6 }}>
                        {Object.entries(filterRules).map(([field, rule]) => {
                          const op = rule.op || rule.operator || "";
                          const val = rule.value;
                          const isNegative = isNegativeOp(op);
                          const isPositive = isPositiveOp(op);
                          const displayText = getRuleDisplay(op, val, field);
                          const formatted = formatRuleValue(op, val, field);
                          
                          return (
                            <div key={field} style={{ display: "flex", flexDirection: "column", padding: "10px 14px", borderRadius: 10, background: isNegative ? "#fef2f2" : isPositive ? "#f0fdf4" : "#fafbff", border: `1.5px solid ${isNegative ? "#fecaca" : isPositive ? "#bbf7d0" : "var(--border)"}`, fontSize: 13, gap: 6 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, flexShrink: 0, textTransform: "capitalize" }}>
                                  {(field || "").replace(/_/g, " ").toLowerCase()}
                                </span>
                                <span style={{ color: "#cbd5e1", fontWeight: 300, flexShrink: 0 }}>→</span>
                                <span style={{ color: isNegative ? "#991b1b" : isPositive ? "#15803d" : "#334155", fontWeight: 500, flex: 1, wordBreak: "break-word" }}>
                                  {displayText}
                                </span>
                              </div>
                              
                              {formatted.hasMore && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleShowAll(field); }}
                                  style={{ fontSize: 11, fontWeight: 600, color: "var(--blue)", background: "none", border: "none", cursor: "pointer", padding: "2px 0", textAlign: "left", alignSelf: "flex-start" }}
                                >
                                  {showAllValues[field] ? t.showLess : `${t.showAll} (${formatted.hiddenCount} ${t.more})`}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {filteringConfig.use_ai && filteringConfig.ai_prompt && (
                    <div style={{ background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", padding: "12px 14px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: .8, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        <span>🧠</span> {t.aiPrompt}
                      </div>
                      <div style={{ fontSize: 12, color: "#475569", whiteSpace: "pre-wrap", lineHeight: 1.6, wordBreak: "break-word" }}>
                        {filteringConfig.ai_prompt}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== 1. SCRAPING STATS ===== */}
            {hasScraping && (googleJobs.total > 0 || parsePipeline.length > 0 || task.scraped_links_count > 0) && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                  <HiSearch size={12} /> {t.scrapingStats}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                  
                  {googleJobs.total > 0 && (
                    <div style={{ background: "#fff", borderRadius: 8, border: "1.5px solid var(--border)", padding: isMobile ? "8px 10px" : "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4, fontWeight: 600 }}>{t.googleJobs}</div>
                      <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "var(--dark)" }}>{googleJobs.success || 0}/{googleJobs.total || 0}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                        {googleJobs.pending || 0} {t.pendingFilter} · {googleJobs.failed || 0} {t.failed}
                      </div>
                    </div>
                  )}
                  {task.search_config.subtype != 'instagram_adb' && task.search_config.subtype != 'facebook_screenshot' && (
                  <div style={{ background: "#fff", borderRadius: 8, border: "1.5px solid var(--border)", padding: isMobile ? "8px 10px" : "10px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4, fontWeight: 600 }}>{t.scrapedLinks}</div>
                    <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "var(--blue)" }}>{(task.scraped_links_count || 0).toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{t.found}</div>
                  </div>
                  )}
                  
                  {parsePipeline.map((stage, idx) => (
                    <div key={idx} style={{ background: "#fff", borderRadius: 8, border: "1.5px solid var(--border)", padding: isMobile ? "8px 10px" : "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4, fontWeight: 600 }}>{stage.label || `${t.stage} ${idx + 1}`}</div>
                      <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "var(--dark)" }}>{stage.success || 0}/{stage.total || 0}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                        {stage.pending || 0} {t.pendingFilter} · {stage.failed || 0} {t.failed}
                      </div>
                    </div>
                  ))}

                  {scrapingTokens > 0 && (
                    <div style={{ background: "#f0f4ff", borderRadius: 8, border: "1.5px solid rgba(59,113,254,.2)", padding: isMobile ? "8px 10px" : "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--blue)", marginBottom: 4, fontWeight: 600 }}>⚡ Scraping Tokens</div>
                      <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "var(--blue)" }}>{formatTokens(scrapingTokens)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== 2. FILTERING STATS ===== */}
            {hasFiltering && fTotal > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                  <HiCheckCircle size={12} /> {t.filteringResults}
                </div>
                
                {Object.keys(filterRules).length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 8, marginBottom: 10 }}>
                    <div style={{ background: "#fff", borderRadius: 8, border: "1.5px solid var(--border)", padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 2 }}>{t.totalRuns}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--dark)" }}>{fTotal}</div>
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                        {fDone} {t.doneFilter} · {fRunning} {t.runningFilter} · {fPending} {t.pendingFilter}
                      </div>
                    </div>
                    
                    <div style={{ background: "#f0fdf4", borderRadius: 8, border: "1.5px solid #bbf7d0", padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: "#15803d", marginBottom: 2 }}>{t.passedFilter}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#15803d" }}>{fPassed}</div>
                      <div style={{ fontSize: 10, color: "#15803d", marginTop: 2 }}>
                        {fTotal > 0 ? Math.round((fPassed / fTotal) * 100) : 0}% {t.ofTotal}
                      </div>
                    </div>
                    
                    <div style={{ background: fRejected > 0 ? "#fef2f2" : "#fff", borderRadius: 8, border: `1.5px solid ${fRejected > 0 ? "#fecaca" : "var(--border)"}`, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: fRejected > 0 ? "#991b1b" : "var(--muted)", marginBottom: 2 }}>{t.rejectedFilter}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: fRejected > 0 ? "#991b1b" : "var(--dark)" }}>{fRejected}</div>
                      <div style={{ fontSize: 10, color: fRejected > 0 ? "#991b1b" : "var(--muted)", marginTop: 2 }}>
                        {fTotal > 0 ? Math.round((fRejected / fTotal) * 100) : 0}% {t.ofTotal}
                      </div>
                    </div>
                    
                    <div style={{ background: fFailed > 0 ? "#fef2f2" : "#fff", borderRadius: 8, border: `1.5px solid ${fFailed > 0 ? "#fecaca" : "var(--border)"}`, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: fFailed > 0 ? "#991b1b" : "var(--muted)", marginBottom: 2 }}>{t.failedFilter}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: fFailed > 0 ? "#991b1b" : "var(--dark)" }}>{fFailed}</div>
                    </div>

                    {fAITokens > 0 && (
                      <div style={{ background: "#f0f4ff", borderRadius: 8, border: "1.5px solid rgba(99,102,241,.3)", padding: "10px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 13, color: "#6366f1", marginBottom: 2 }}>🤖 AI Tokens</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "#6366f1" }}>{formatTokens(fAITokens)}</div>
                      </div>
                    )}
                  </div>
                )}

                {(filteringConfig?.use_ai || fAIPassed > 0 || fAIRejected > 0) && (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 8 }}>
                      <div style={{ background: "#f0fdf4", borderRadius: 8, border: "1.5px solid #bbf7d0", padding: "10px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 13, color: "#15803d", marginBottom: 2 }}>{t.aiPassed || "AI Passed"}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "#15803d" }}>{fAIPassed}</div>
                      </div>
                      
                      <div style={{ background: fAIRejected > 0 ? "#fef2f2" : "#fff", borderRadius: 8, border: `1.5px solid ${fAIRejected > 0 ? "#fecaca" : "var(--border)"}`, padding: "10px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 13, color: fAIRejected > 0 ? "#991b1b" : "var(--muted)", marginBottom: 2 }}>{t.aiRejected || "AI Rejected"}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: fAIRejected > 0 ? "#991b1b" : "var(--dark)" }}>{fAIRejected}</div>
                      </div>

                      {fAITokens > 0 && (
                        <div style={{ background: "#f0f4ff", borderRadius: 8, border: "1.5px solid rgba(99,102,241,.3)", padding: "10px 12px", textAlign: "center" }}>
                          <div style={{ fontSize: 13, color: "#6366f1", marginBottom: 2 }}>🤖 AI Tokens</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: "#6366f1" }}>{formatTokens(fAITokens)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== KEYWORDS ===== */}
            {task.keywords?.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                  <HiSearch size={12} /> {t.keywords} ({task.keywords.length})
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexWrap: "wrap", gap: 6, border: "1.5px solid var(--border)", borderRadius: 8, padding: isMobile ? 6 : 8, background: "#fff" }}>
                  {task.keywords.map((kw) => (
                    <span key={kw.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: isMobile ? "4px 8px" : "5px 10px", borderRadius: 8, background: kw.is_complete ? "#f0fdf4" : "#f0f4ff", border: `1.5px solid ${kw.is_complete ? "#bbf7d0" : "rgba(59,113,254,.2)"}`, fontSize: isMobile ? 11 : 12, fontWeight: 600, color: kw.is_complete ? "#15803d" : "var(--blue)" }}>
                      {kw.keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ===== SEARCH CONFIG ===== */}
            {task.search_config && task.search_config.subtype != 'instagram_adb' && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                  <HiCog size={12} /> {t.searchConfig}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {task.search_config.search_language && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 8, background: "#fff", border: "1.5px solid var(--border)", fontSize: 11, fontWeight: 500 }}>
                      <HiGlobe size={12} /> {task.search_config.search_language.toUpperCase()}
                    </span>
                  )}
                  {task.search_config.search_periods?.map(p => (
                    <span key={p} style={{ padding: "4px 10px", borderRadius: 8, background: "#fff", border: "1.5px solid var(--border)", fontSize: 11, fontWeight: 500 }}>{PERIOD_LABELS[p] || p}</span>
                  ))}
                  {task.search_config.max_pages_per_query && (
                    <span style={{ padding: "4px 10px", borderRadius: 8, background: "#fff", border: "1.5px solid var(--border)", fontSize: 11, fontWeight: 500 }}>{t.maxPages}: {task.search_config.max_pages_per_query}</span>
                  )}
                </div>
              </div>
            )}

            {/* ===== ADDITIONAL INFO ===== */}
            {hasAdditionalInfo && !hasFiltering && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                  ⚙️ {t.additionalSettings}
                </div>
                <div style={{ background: "#fff", borderRadius: 8, border: "1.5px solid var(--border)", padding: "10px 14px", display: "grid", gap: 8 }}>
                  {isInstagramPages && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, background: instagramPostsIncluded ? "#f0fdf4" : "#fef2f2", border: `1.5px solid ${instagramPostsIncluded ? "#bbf7d0" : "#fecaca"}` }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: instagramPostsIncluded ? "#15803d" : "#991b1b" }}>
                          {t.scrapeAuthorPages}: {instagramPostsIncluded ? t.enabled.toUpperCase() : t.disabled.toUpperCase()}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                          {instagramPostsIncluded ? t.authorPagesEnabledDesc : t.authorPagesDisabledDesc}
                        </div>
                      </div>
                    </div>
                  )}
                  {Object.entries(additionalInfo).map(([key, value]) => {
                    if (key === "instagram_posts_included" && isInstagramPages) return null;
                    if (value === null || value === undefined) return null;
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                        <code style={{ fontSize: 11, fontWeight: 600, color: "var(--blue)", background: "rgba(59,113,254,.08)", padding: "2px 8px", borderRadius: 4, fontFamily: "monospace", whiteSpace: "nowrap" }}>{key}</code>
                        <span style={{ fontSize: 12, color: "var(--dark)", wordBreak: "break-word" }}>
                          {typeof value === "boolean" ? (value ? `✅ ${t.enabled}` : `❌ ${t.disabled}`) : typeof value === "object" ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== TIMESTAMPS ===== */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 8, fontSize: 11, color: "var(--muted)" }}>
              <div><div style={{ fontWeight: 600, fontSize: 10, marginBottom: 2 }}>{t.created.toUpperCase()}</div>{formatDate(task.created_at)}</div>
              <div><div style={{ fontWeight: 600, fontSize: 10, marginBottom: 2 }}>{t.started.toUpperCase()}</div>{formatDate(task.started_at)}</div>
              <div><div style={{ fontWeight: 600, fontSize: 10, marginBottom: 2 }}>{t.updated.toUpperCase()}</div>{formatDate(task.updated_at)}</div>
              <div><div style={{ fontWeight: 600, fontSize: 10, marginBottom: 2 }}>{t.finished.toUpperCase()}</div>{formatDate(task.finished_at)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResults && (
        <ResultsModal 
          task={task} 
          onClose={() => setShowResults(false)} 
          t={t} 
          isMobile={isMobile} 
        />
      )}


  {showKeywordStats && (
    <KeywordStatsModal 
      task={task}
      onClose={() => setShowKeywordStats(false)}
      t={t}
      isMobile={isMobile}
    />
  )}
    </div>
  );
}

// ==================== PAGINATION COMPONENT ====================
function Pagination({ currentPage, totalPages, onPageChange, isMobile }) {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const maxVisible = isMobile ? 3 : 5;
    const pages = [];
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    
    for (let i = start; i <= end; i++) pages.push(i);
    
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: 20,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: '#fff',
        borderRadius: 12,
        border: '2px solid var(--border)',
        padding: '6px 8px',
      }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={{
            minWidth: 36,
            height: 36,
            padding: '0 6px',
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            color: currentPage === 1 ? '#cbd5e1' : 'var(--dark)',
            fontSize: 16,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: currentPage === 1 ? 0.4 : 1,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            if (currentPage !== 1) e.currentTarget.style.background = 'rgba(59,113,254,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          «
        </button>
        
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            minWidth: 36,
            height: 36,
            padding: '0 6px',
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            color: currentPage === 1 ? '#cbd5e1' : 'var(--dark)',
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: currentPage === 1 ? 0.4 : 1,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            if (currentPage !== 1) e.currentTarget.style.background = 'rgba(59,113,254,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <HiChevronLeft size={18} />
        </button>

        {getVisiblePages().map((page, idx) => 
          page === '...' ? (
            <span key={`dots-${idx}`} style={{ 
              width: 36,
              height: 36,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#94a3b8',
              fontSize: 14,
              userSelect: 'none',
            }}>…</span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              style={{
                minWidth: 36,
                height: 36,
                padding: '0 6px',
                borderRadius: 8,
                border: 'none',
                background: page === currentPage ? 'var(--blue)' : 'transparent',
                cursor: 'pointer',
                color: page === currentPage ? '#fff' : 'var(--dark)',
                fontSize: 14,
                fontWeight: page === currentPage ? 600 : 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (page !== currentPage) {
                  e.currentTarget.style.background = 'rgba(59,113,254,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (page !== currentPage) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            minWidth: 36,
            height: 36,
            padding: '0 6px',
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            color: currentPage === totalPages ? '#cbd5e1' : 'var(--dark)',
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: currentPage === totalPages ? 0.4 : 1,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages) e.currentTarget.style.background = 'rgba(59,113,254,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <HiChevronRight size={18} />
        </button>

        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          style={{
            minWidth: 36,
            height: 36,
            padding: '0 6px',
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            color: currentPage === totalPages ? '#cbd5e1' : 'var(--dark)',
            fontSize: 16,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: currentPage === totalPages ? 0.4 : 1,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages) e.currentTarget.style.background = 'rgba(59,113,254,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          »
        </button>
      </div>
    </div>
  );
}

// ==================== Главный компонент ====================
export function TasksList({ userInfo, onAdd, onResendVerify, onVisualize, onForceStopTopUp }) {
  const { lang } = React.useContext(LangContext);
  const fullT = lang === "pl" ? pl : en;
  const t = fullT.tasksListPage || {};
  const isMobile = useIsMobile();

  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVerify, setShowVerify] = useState(false);
  
  // ===== PAGINATION STATE =====
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [renameModal, setRenameModal] = useState({ open: false, taskId: null, currentName: "" });
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(false);


  const pageSize = 10;

  const API_BASE = "https://flautreach.com/flautreach/api";

  const userInfoFromCookie = getCookieJSON('userInfo');
  const effectiveUserInfo = userInfo || userInfoFromCookie;


  const handleOpenRename = (task) => {
  setRenameModal({ open: true, taskId: task.task_id, currentName: task.task_name });
  setNewName(task.task_name);
};


const handleRename = async () => {
  if (!newName.trim() || newName.trim() === renameModal.currentName) {
    setRenameModal({ open: false, taskId: null, currentName: "" });
    return;
  }
  
  try {
    setRenaming(true);
    const token = getCookie("authToken");
    const res = await fetch(`${API_BASE}/tasks/${renameModal.taskId}/rename/`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name: newName.trim() }),
    });
    
    if (!res.ok) throw new Error("Failed to rename");
    
    setRenameModal({ open: false, taskId: null, currentName: "" });
    fetchTasks(currentPage);
  } catch (err) {
    console.error("Rename error:", err);
  } finally {
    setRenaming(false);
  }
};


  const fetchTasks = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const token = getCookie("authToken");
      if (!token) { 
        setError(t.notAuthenticated); 
        setLoading(false); 
        return; 
      }

      const res = await fetch(
        `${API_BASE}/tasks/fullstats/?page=${page}&page_size=${pageSize}`,
        {
          headers: { 
            "Authorization": `Bearer ${token}`, 
            "Content-Type": "application/json" 
          },
        }
      );
      
      if (!res.ok) throw new Error(`${t.fetchFailed} (${res.status})`);
      
      const data = await res.json();
      
      setTasks(data.tasks || []);
      setSummary(data.summary || null);
      
      // Используем total_tasks из summary для подсчета страниц
      const totalTasksCount = data.summary?.total_tasks || 0;
      setTotalCount(totalTasksCount);
      setTotalPages(Math.ceil(totalTasksCount / pageSize) || 1);
      setCurrentPage(page);
      
    } catch (err) { 
      console.error("Fetch error:", err); 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  }, [API_BASE, pageSize, t]);

  useEffect(() => { 
    fetchTasks(1); 
  }, [fetchTasks]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchTasks(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStart = async (taskId) => {
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/${taskId}/start/`, {
        method: "POST", 
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) fetchTasks(currentPage);
    } catch (err) { console.error(err); }
  };

  const handleStop = async (taskId) => {
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/${taskId}/pause/`, {
        method: "POST", 
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) fetchTasks(currentPage);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/${taskId}/`, {
        method: "DELETE", 
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) {
        if (tasks.length === 1 && currentPage > 1) {
          fetchTasks(currentPage - 1);
        } else {
          fetchTasks(currentPage);
        }
      }
    } catch (err) { console.error(err); }
  };

  const handleVisualize = (task) => { 
    if (onVisualize) onVisualize(task); 
  };

  const handleEdit = (task) => {
  handleOpenRename(task);
};

  const handleDuplicate = (task) => {
    onAdd({ prefillTask: task });
  };

  const handleCreateFilter = (task) => {
    console.log("Create filtering task from:", task.task_id);
  };

  const handleForceStopTopUp = (tokenType) => {
    if (onForceStopTopUp) {
      onForceStopTopUp(tokenType);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: isMobile ? 30 : 60 }}>
        <div style={{ fontSize: isMobile ? 30 : 40, marginBottom: 12 }}>⚠️</div>
        <h3 style={{ marginBottom: 8, fontSize: isMobile ? 16 : 18 }}>{t.failedToLoadTasks}</h3>
        <p style={{ color: "var(--muted)", marginBottom: 16, fontSize: isMobile ? 13 : 14 }}>{error}</p>
        <button className="btn-primary" onClick={() => fetchTasks(currentPage)} style={{ maxWidth: 200, margin: "0 auto" }}>
          {t.retry}
        </button>
      </div>
    );
  }

  return (
    <div>
      {showVerify && (
        <div className="verify-banner">
          <span className="verify-banner-icon">⚠️</span>
          <p>
            {t.verifyBanner} <strong>{userInfo?.email}</strong>{" "}
            {t.verifyBanner2}{" "}
            <button className="link-btn" onClick={onResendVerify}>{t.resendVerify}</button>
          </p>
        </div>
      )}

      <div className="rules-header" style={{ 
        flexDirection: isMobile ? "column" : "row", 
        gap: isMobile ? 12 : 0, 
        alignItems: isMobile ? "stretch" : "center",
        marginBottom: 20,
      }}>
        <div>
          <h2 style={{ fontSize: isMobile ? 16 : 18, margin: 0 }}>{t.myTasks}</h2>
          <p style={{ fontSize: isMobile ? 11 : 13, margin: '4px 0 0 0', color: 'var(--muted)' }}>
            {summary ? (
              <>
                {summary.active_tasks} {t.active}, {summary.total_tasks} {t.total}
                {summary.overall_progress !== undefined && (
                  <> · {summary.overall_progress}% {t.complete}</>
                )}
              </>
            ) : (
              <>
                {tasks.filter(task => task.task_status === "running").length} {t.running}, {tasks.length} {t.total}
              </>
            )}
          </p>
        </div>
        <button className="btn-add" onClick={() => onAdd()} style={{ width: isMobile ? "100%" : "auto" }}>
          <span style={{ fontSize: 18 }}>＋</span> {t.newTask}
        </button>
      </div>

      

      {tasks.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 20,
          padding: '14px 18px',
          background: 'rgba(126,153,225,0.04)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(59,113,254,0.1)',
          borderRadius: 12,
          fontSize: 13,
          color: '#475569',
          lineHeight: 1.5,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚡</span>
          <span style={{ flex: 1 }}>
            {t.queueNotice}{' '}
            <a 
              href="/balance" 
              style={{ 
                color: 'var(--blue)', 
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {t.topUpBalance}
            </a>
            {' '}{t.fasterProcessing}
          </span>
        </div>
      )}

      {tasks.length === 0 ? (
        <EmptyState onAdd={onAdd} />
      ) : (
        <div className="rules-grid" style={{ marginBottom: 20 }}>
          {tasks.map((task) => (
            <TaskCard 
              key={task.task_id} 
              task={task} 
              t={t}
              onStart={handleStart} 
              onStop={handleStop}
              onDelete={handleDelete} 
              onVisualize={handleVisualize}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onCreateFilter={handleCreateFilter}
              onForceStopTopUp={handleForceStopTopUp}
              userInfo={effectiveUserInfo}
              API_BASE={API_BASE}
            />
          ))}
        </div>
      )}

      {/* ===== PAGINATION BOTTOM ===== */}
      {totalCount > pageSize && (
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          onPageChange={handlePageChange}
          isMobile={isMobile}
          t={t}
        />
      )}
      
      {/* ===== RENAME MODAL ===== */}
{renameModal.open && (
  <div style={{
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16
  }}>
    <div style={{
      background: "#fff", borderRadius: 16, width: "100%",
      maxWidth: 420, padding: 24,
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    }}>
      <h3 style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 700 }}>
        {t.rename.renameTask }
      </h3>
      <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
        {t.rename.renameTaskDesc }
      </p>
      
      <input
        type="text"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleRename();
          if (e.key === "Escape") setRenameModal({ open: false, taskId: null, currentName: "" });
        }}
        autoFocus
        style={{
          width: "100%", padding: "12px 14px",
          borderRadius: 10, border: "1.5px solid var(--border)",
          fontSize: 14, outline: "none",
          boxSizing: "border-box",
        }}
        placeholder={t.rename.renamePlaceholder}
      />
      
      <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
        <button
          onClick={() => setRenameModal({ open: false, taskId: null, currentName: "" })}
          style={{
            padding: "8px 16px", borderRadius: 8,
            border: "1.5px solid var(--border)", background: "#fff",
            cursor: "pointer", fontSize: 14, fontWeight: 500,
          }}
        >
          {t.rename.cancel }
        </button>
        <button
          onClick={handleRename}
          disabled={renaming || !newName.trim()}
          style={{
            padding: "8px 16px", borderRadius: 8,
            border: "none", background: "var(--blue)",
            color: "#fff", cursor: renaming ? "not-allowed" : "pointer",
            fontSize: 14, fontWeight: 600, opacity: renaming ? 0.6 : 1,
          }}
        >
          {renaming ? (t.rename.saving) : (t.rename.save )}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}